import PropTypes from 'prop-types';
import { components } from 'react-select';

import { FontAwesomeIcon } from '@hivebrite/design-system/FontAwesomeIcon';

import ReactSelect from '../ReactSelect';

const filterOption = (option, inputValue) => {
  // Display custom search option when user starts typing on input
  if (option?.data?.type === 'search') {
    return !!inputValue;
  }

  // Render all classic options if user doesn't start typing on input
  if (!inputValue) {
    return true;
  }

  const formattedLabel = option?.label?.toLowerCase();
  const formattedInput = inputValue?.toLowerCase();

  // Render filtered other options if user starts typing on input
  return formattedLabel?.includes(formattedInput?.toLowerCase());
};

const GroupHeading = ({ data, selectProps }) => {
  // No top divider for search option
  if (data?.type === 'search') {
    return null;
  }

  return selectProps?.inputValue ? (
    <hr style={{ marginTop: '0', marginBottom: '4px' }} />
  ) : null;
};

const Option = (props) => {
  const {
    data: { name, type },
    data,
    innerProps,
    isSelected,
    options,
    selectProps,
    setValue,
    value,
  } = props;

  const groupOfOptions = options.find((groupOfOptions) => {
    if (groupOfOptions?.options) {
      return groupOfOptions.options.find((options) => options.id === value);
    }

    return null;
  });
  const renderLabel = groupOfOptions?.renderLabel;

  // Force onClick behavior for search option
  const newPropsForSearch = {
    ...props,
    innerProps: {
      ...innerProps,
      onClick: (event) => {
        if (selectProps?.onSearchChange) {
          selectProps?.onSearchChange(selectProps?.inputValue);
        }

        event.preventDefault();
        event.stopPropagation();

        selectProps?.onMenuClose();
      },
    },
  };

  // Force onClick behavior for other options
  const newPropsForOtherOptions = {
    ...props,
    innerProps: {
      ...innerProps,
      onClick: (event) => {
        // Reset search value when an other option is selected
        setValue('');
        if (
          selectProps?.onSearchChange &&
          selectProps?.shouldResetFormikSearchValueOnOtherOptionSelection
        ) {
          selectProps?.onSearchChange('');
        }

        if (selectProps?.onOtherOptionsChange) {
          selectProps?.onOtherOptionsChange({ data, groupOfOptions });
        }

        event.preventDefault();
        event.stopPropagation();

        selectProps?.onMenuClose();
      },
    },
  };

  if (type === 'search' && selectProps?.inputValue) {
    return (
      <li
        aria-posinset={data.id}
        aria-selected={isSelected}
        aria-setsize={options.length}
        role="option"
        tabIndex={-1}
      >
        <components.Option {...newPropsForSearch}>
          <div
            data-testid="react-select-creatable"
            className="flex flex--center"
          >
            {renderLabel ? (
              renderLabel(selectProps?.inputValue)
            ) : (
              <>
                <div className="push-quarter--right">
                  <FontAwesomeIcon height="16px" iconName="magnifying-glass" />
                </div>
                {s__('Filters|Search for "%{value}"').i__({
                  value: selectProps?.inputValue,
                })}
              </>
            )}
          </div>
        </components.Option>
      </li>
    );
  }

  return (
    <li
      aria-posinset={data.id}
      aria-selected={isSelected}
      aria-setsize={options.length}
      role="option"
      tabIndex={-1}
    >
      <components.Option {...newPropsForOtherOptions}>
        <div
          data-testid="react-select-option"
          dangerouslySetInnerHTML={{
            __html: (renderLabel ? renderLabel(name) : name)?.toString(),
          }}
        />
      </components.Option>
    </li>
  );
};

const ReactSelectSearchAndOptions = ({
  placeholder,
  noOptionsMessage,
  onSearchChange,
  onInnerInputChange,
  onChange,
  shouldResetFormikSearchValueOnOtherOptionSelection = false,
  ...otherProps
}) => (
  <ReactSelect
    placeholder={placeholder || __('Companies')}
    noOptionsMessage={noOptionsMessage}
    components={{
      Option,
      GroupHeading,
    }}
    createOptionPosition="first"
    creatable
    onChange={onChange}
    onSearchChange={onSearchChange}
    filterOption={filterOption}
    onInnerInputChange={onInnerInputChange}
    shouldResetFormikSearchValueOnOtherOptionSelection={
      shouldResetFormikSearchValueOnOtherOptionSelection
    }
    {...otherProps}
  />
);

ReactSelectSearchAndOptions.propTypes = {
  noOptionsMessage: PropTypes.func,
  placeholder: PropTypes.string,
  shouldResetFormikSearchValueOnOtherOptionSelection: PropTypes.bool,
};

export default ReactSelectSearchAndOptions;
